import gql from "graphql-tag";
import {
  AttributeFilter,
  ChosenAttributesInput,
  PageInfo,
  ProductTaxonomyFilterInput,
} from "../types/generalTypes";
import { ProductProps } from "../types/productTypes";

export interface GetProductsVariables {
  limit: number;
  before?: string;
  after?: string;
  category?: string;
  orderby?: { field: string; order?: string }[];
  taxonomyFilter?: ProductTaxonomyFilterInput[];
  minPrice?: number;
  maxPrice?: number;
  onSale?: boolean;
  day?: number;
  month?: number;
  year?: number;
  ids?: number[];
  categoryIn?: string[];
}

export interface GetProductsResponse {
  products: {
    nodes: ProductProps[];
    pageInfo?: PageInfo;
  };
}

export const getProducts = gql`
  query getProducts(
    $last: Int
    $limit: Int
    $after: String
    $before: String
    $category: String
    $orderby: [ProductsOrderbyInput]
    $taxonomyFilter: [ProductTaxonomyFilterInput]
    $minPrice: Float
    $maxPrice: Float
    $onSale: Boolean
    $day: Int
    $month: Int
    $year: Int
    $ids: [Int]
    $categoryIn: [String]
  ) {
    products(
      last: $last
      first: $limit
      after: $after
      before: $before
      where: {
        category: $category
        categoryIn: $categoryIn
        orderby: $orderby
        taxonomyFilter: { or: $taxonomyFilter }
        minPrice: $minPrice
        maxPrice: $maxPrice
        onSale: $onSale
        dateQuery: { after: { day: $day, month: $month, year: $year } }
        include: $ids
        stockStatus: IN_STOCK
      }
    ) {
      nodes {
        id
        slug
        databaseId
        name
        type
        description

        ... on VariableProduct {
          id
          name
          regularPrice
          salePrice
          onSale
          variations(first: 100, where : {stockStatus: IN_STOCK}) {
            nodes {
              id
              databaseId
              stockQuantity
              image {
                slug
              }
              attributes {
                nodes {
                  id
                  name
                  value
                }
              }
            }
          }
        }
        ... on SimpleProduct {
          id
          name
          stockStatus
          stockQuantity
          regularPrice
          salePrice
          onSale
        }
        image {
          id
          slug
          mediaItemUrl
          sourceUrl(size: _630X500)
        }
        galleryImages(first: 100) {
          nodes {
            id
            slug
            sourceUrl(size: LARGE)
          }
        }
        productBrands {
          nodes {
            name
            slug
          }
        }
        productCategories {
          nodes {
            id
            slug
            name
            databaseId
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;

export interface ShopProductCategory {
  id: string;
  databaseId: number;
  link: string;
  slug: string;
  name: string;
  children: {
    nodes: ShopProductCategory[];
  };
}

export interface GetShopProductCategoriesVariables {
  customTaxonomy?: ChosenAttributesInput[];
}

export interface getMenuVariables {}

export interface GetShopProductCategoriesResponse {
  productCategories: {
    nodes: ShopProductCategory[];
  };
}

export interface GetAllProductCategoriesResponse {
  productCategories: {
    nodes: ShopProductCategoryFlat[];
  };
}

export interface ShopProductCategoryFlat {
  id: string;
  databaseId: number;
  link: string;
  slug: string;
  name: string;
  parent?: {
    node: {
      id: string;
      databaseId: number;
      slug: string;
      name: string;
    };
  };
  children: {
    nodes: ShopProductCategoryFlat[];
  };
}

export interface getMenuResponse {
  products: {
    nodes: [
      {
        productCategories: {
          nodes: ShopProductCategory[];
        };
      }
    ];
  };
}

export const getProductCategories = gql`
  query getProductCategories($customTaxonomy: [ChosenAttributesInput]) {
    productCategories(
      first: 100
      where: {
        hideEmpty: true
        exclude: "15"
        customTaxonomy: $customTaxonomy
        parent: 0
      }
    ) {
      nodes {
        id
        databaseId
        link
        name
        slug
        description
        image {
          databaseId
          mediaItemUrl
          sourceUrl
        }
        children(first: 100, where: { hideEmpty: true, customTaxonomy: $customTaxonomy }) {
          nodes {
            id
            databaseId
            name
            link
            slug
            description
            image {
              databaseId
              mediaItemUrl
              sourceUrl
            }
            children(
              where: { hideEmpty: true, customTaxonomy: $customTaxonomy }
            ) {
              nodes {
                id
                databaseId
                name
                link
                slug
                description
                image {
                  databaseId
                  mediaItemUrl
                  sourceUrl
                }
                children(
                  where: { hideEmpty: true, customTaxonomy: $customTaxonomy }
                ) {
                  nodes {
                    id
                    databaseId
                    name
                    link
                    slug
                    description
                    image {
                      databaseId
                      mediaItemUrl
                      sourceUrl
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAllProductCategories = gql`
  query getAllProductCategories {
    productCategories(
      first: 1000
      where: {
        hideEmpty: true
        exclude: "15"
      }
    ) {
      nodes {
        id
        databaseId
        link
        name
        slug
        parent {
          node {
            id
            databaseId
            slug
            name
          }
        }
        children(first: 100, where: { hideEmpty: true }) {
          nodes {
            id
            databaseId
            name
            link
            slug
          }
        }
      }
    }
  }
`;

export const getMenu = gql`
  query paGjinias {
    productCategories(first: 1) {
      nodes {
        id
        name
      }
    }
  }
`;

export const GET_MENU = gql`
  query getMenu {
    productCategories(first: 1, where: { hideEmpty: true, parent: 0 }) {
      nodes {
        id
        name
        slug
      }
    }
  }
`;

export const GET_MENU_CHILDS = gql`
  query getMenuChilds($id: ID!) {
    productCategory(id: $id) {
      name
      id
      slug
    }
  }
`;

export interface GetAttributeFiltersVariables {
  chosenAttributes: ChosenAttributesInput[];
}

export interface GetAttributeFiltersResponse {
  attributeFilters: AttributeFilter[];
}

export const getAttributeFilters = gql`
  query getAttributeFilters($chosenAttributes: [ChosenAttributesInput]) {
    attributeFilters(chosenAttributes: $chosenAttributes) {
      id
      name
      label
      slug
      terms {
        id
        count
        name
        slug
      }
    }
  }
`;

export interface GetCategoryBySlugVariables {
  slug: string;
}

export interface GetCategoryBySlugResponse {
  productCategory: {
    image: {
      mediaItemUrl: string
    }
    categoryBannerImage: {
      bannerImage: {
        mediaItemUrl: string
      }
    }
  }
}

export const getCategoryBySlug = gql`
  query getCategoryBySlug($slug: ID!) {
    productCategory(id: $slug, idType: SLUG) {
      image {
        mediaItemUrl
      }
      categoryBannerImage {
        bannerImage {
          mediaItemUrl
        }
      }
    }
  }
`;

export const NEW_PRODUCTS = gql`
  query newProducts {
    generalOptions {
      __typename
    }
  }
`;
